<template>
  <section class="training-info-section">
    <training-modal :visible.sync="visibleEditModal" />

    <section-divider title="Training">
      <template slot="actions">
        <a-button
          v-if="
            hasUncontrolledPermission('manage_associates', {
              id: associate.id
            })
          "
          class="flex gap-2 align-items-center"
          @click="visibleEditModal = true"
        >
          <a-icon width="16px" height="16px" icon="material-symbols:edit-square-outline" />
          Edit
        </a-button>
      </template>
    </section-divider>

    <div class="training-info-grid">
      <div class="training-info-field">
        <span>Training Period</span>
        <div class="flex gap-1 align-items-center">
          <a-tag class="m-0" :color="associate.is_in_training_period ? '#10b981' : '#999999'">
            {{ associate.is_in_training_period ? 'Active' : 'Inactive' }}
          </a-tag>
          <export-button
            v-if="showExportButton"
            :only-icon="true"
            :loading="loadingExport"
            @send="sendReport"
            @download="downloadReport"
          />
        </div>
      </div>
      <template v-if="associate?.training_started_at">
        <div class="training-info-field">
          <span>Training Started At</span>
          <strong>{{ formatDateString(associate?.training_started_at) }}</strong>
        </div>
        <div class="training-info-field">
          <span>Training Ended At</span>
          <strong>{{ formatDateString(associate?.training_ended_at) }}</strong>
        </div>
      </template>
    </div>
  </section>
</template>

<script>
import ExportButton from '@/components/export-button.vue'
import { Button, notification, Tag } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import util from '@/util'
import moment from 'moment-timezone'
import permissionMixin from '@/mixins/permissionMixin'
import TrainingModal from './training-modal.vue'
import SectionDivider from '@/components/ui/section-divider.vue'

const { mapState: profileState, mapActions: profileActions } = createNamespacedHelpers('profile')
const { mapState: documentState } = createNamespacedHelpers('document')

export default {
  components: {
    'a-button': Button,
    'a-tag': Tag,
    'export-button': ExportButton,
    'training-modal': TrainingModal,
    'section-divider': SectionDivider
  },
  data() {
    return { loadingExport: false, visibleEditModal: false }
  },
  mixins: [permissionMixin],
  computed: {
    ...profileState({
      associate: state => state.associate
    }),
    ...documentState({
      associateDynamicForm: state => state.associateDynamicForm
    }),

    showExportButton() {
      const skills_check = this.associateDynamicForm.annual_skills_check ?? this.associateDynamicForm.skills_check

      return (
        skills_check &&
        !this.associate.is_in_training_period &&
        !['expired', 'awaiting_signature'].includes(skills_check.status)
      )
    }
  },
  methods: {
    ...profileActions(['sendExportedAssociate', 'downloadExportedAssociate', 'updateAssociate', 'setAssociate']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    formatDateString(dateString) {
      return dateString ? moment(dateString).utc().format('MM/DD/YYYY') : ''
    },

    async sendReport(email) {
      this.loadingExport = true

      const payload = {
        skills_check: true,
        email: email.toLowerCase(),
        associate_id: this.associate.id
      }

      try {
        await this.sendExportedAssociate(payload)

        this.showNotification('success', 'Success', 'Skills Check successfully sent to email')
      } catch (error) {
        this.showNotification('error', 'Error', error.message || error)
      } finally {
        this.loadingExport = false
      }
    },

    async downloadReport() {
      this.loadingExport = true

      const payload = {
        skills_check: true,
        associate_id: this.associate.id
      }

      try {
        const url = await this.downloadExportedAssociate(payload)

        util.downloadFile(url)
      } catch (error) {
        this.showNotification('error', 'Error', error.message || error)
      } finally {
        this.loadingExport = false
      }
    }
  }
}
</script>

<style scoped>
.training-info-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.training-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 8px;
}

.training-info-field {
  min-width: 0;
}

.training-info-field > span {
  display: block;
  margin-bottom: 4px;
  color: var(--text-color-secondary);
  font-size: 12px;
}

.training-info-field > strong {
  display: block;
  overflow-wrap: anywhere;
}
</style>
